import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { currencyFormat } from 'helpers/utils';
import { ColumnDef } from '@tanstack/react-table';
import { Order, ProductItem } from '../types';
import ProductImage from '../ProductImage';

type OrderColumnDef = ColumnDef<Order, any> & {
  visible?: boolean;
  accessorKey?: string;
  id?: string;
}

type ProductColumnDef = ColumnDef<ProductItem, any> & {
  visible?: boolean;
  accessorKey?: string;
  id?: string;
}

/**
 * Custom hook to define column configurations for Orders and Products tables
 */
export function useTableColumns() {
  const { t } = useTranslation();
  
  // Define columns for the orders table
  const orderColumns: OrderColumnDef[] = useMemo(() => [
    {
      id: 'order_number',
      accessorKey: 'orderNumber',
      header: t('transactions.detail.orderColumns.orderNumber', 'Order Number'),
      visible: true,
      cell: ({ row: { original } }) => (
        <Link 
          to={`/orders/${original.order_id}`} 
          className="fw-semibold text-decoration-none"
        >
          #{original.order_number}
        </Link>
      ),
      meta: {
        headerProps: { style: { width: '10%' }, className: 'ps-4' },
        cellProps: { className: 'ps-4 text-body fw-semibold' }
      }
    },
    {
      id: 'orderDate',
      accessorKey: 'orderDate',
      header: t('transactions.detail.orderColumns.orderDate', 'Order Date'),
      visible: true,
      cell: ({ row: { original } }) => (
        <span className="fs-9 text-capitalize fw-semibold">
          {original.orderDate}
        </span>
      ),
      meta: {
        headerProps: { style: { width: '15%' }, className: 'ps-4' },
        cellProps: { className: 'ps-4 text-body-tertiary' }
      }
    },
    {
      id: 'totalAmount',
      accessorKey: 'totalAmount',
      header: t('transactions.detail.orderColumns.totalAmount', 'Total Amount'),
      visible: true,
      cell: ({ row: { original } }) => (
        <span className="currency-value fw-semibold">
          {currencyFormat(original.totalAmount)}
        </span>
      ),
      meta: {
        headerProps: { style: { width: '15%' }, className: 'ps-4' },
        cellProps: { className: 'ps-4 fw-semibold text-body-highlight' }
      }
    },
    {
      id: 'commission',
      accessorKey: 'commission',
      header: t('transactions.detail.orderColumns.commission', 'Commission'),
      visible: true,
      cell: ({ row: { original } }) => (
        <span className="currency-value fw-semibold">
          {currencyFormat(original.commission)}
        </span>
      ),
      meta: {
        headerProps: { style: { width: '15%' }, className: 'ps-4' },
        cellProps: { className: 'ps-4 fw-semibold text-body-highlight' }
      }
    },
    {
      id: 'netEarnings',
      accessorKey: 'netEarnings',
      header: t('transactions.detail.orderColumns.netEarnings', 'Net Earnings'),
      visible: true,
      cell: ({ row: { original } }) => (
        <span className="currency-value fw-semibold">
          {currencyFormat(original.netEarnings)}
        </span>
      ),
      meta: {
        headerProps: { style: { width: '15%' }, className: 'ps-4' },
        cellProps: { className: 'ps-4 fw-semibold text-body-highlight' }
      }
    }
  ], []);
  
  // Define columns for the products table
  const productColumns: ProductColumnDef[] = useMemo(() => [
    {
      id: 'order_number',
      accessorKey: 'order_number',
      header: t('transactions.detail.productColumns.orderNumber', 'Order Number'),
      visible: true,
      cell: ({ row: { original } }) => (
        <Link 
          to={`/orders/${original.order_id}`} 
          className="fw-semibold text-decoration-none"
        >
          #{original.order_number}
        </Link>
      ),
      meta: {
        headerProps: { style: { width: '10%' }, className: 'ps-4' },
        cellProps: { className: 'ps-4 text-body fw-semibold' }
      }
    },
    {
      id: 'product_name',
      accessorKey: 'product_name',
      header: t('transactions.detail.productColumns.productDetails', 'Product Details'),
      visible: true,
      cell: ({ row: { original } }) => (
        <div className="d-flex align-items-center">
          <ProductImage image={original.image} productName={original.product_name} />
          <div className="d-flex flex-column">
            <span className="fw-semibold line-clamp-3 text-body-highlight">
              {original.product_name}
            </span>
          </div>
        </div>
      ),
      meta: {
        headerProps: { style: { width: '20%' }, className: 'ps-4' },
        cellProps: { className: 'ps-4 text-body' }
      }
    },
    {
      id: 'sku',
      accessorKey: 'sku',
      header: t('transactions.detail.productColumns.sku', 'SKU'),
      visible: true,
      cell: ({ row: { original } }) => (
        <span className="fs-9 text-body-tertiary">
          {original.sku}
        </span>
      ),
      meta: {
        headerProps: { style: { width: '10%' }, className: 'ps-4' },
        cellProps: { className: 'ps-4 text-body-tertiary' }
      }
    },
    {
      id: 'price',
      accessorKey: 'price',
      header: t('transactions.detail.productColumns.price', 'Price'),
      visible: true,
      cell: ({ row: { original } }) => (
        <span className="currency-value fw-semibold">
          {currencyFormat(original.price)}
        </span>
      ),
      meta: {
        headerProps: { style: { width: '8%' }, className: 'ps-4' },
        cellProps: { className: 'ps-4 text-body-highlight' }
      }
    },
    {
      id: 'qty',
      accessorKey: 'qty',
      header: t('transactions.detail.productColumns.qty', 'Qty'),
      visible: true,
      cell: ({ row: { original } }) => (
        <span className="fw-semibold">
          {original.qty}
        </span>
      ),
      meta: {
        headerProps: { style: { width: '5%' }, className: 'ps-4' },
        cellProps: { className: 'ps-4 text-body' }
      }
    },
    {
      id: 'total_price',
      accessorKey: 'total_price',
      header: t('transactions.detail.productColumns.totalPrice', 'Total Price'),
      visible: true,
      cell: ({ row: { original } }) => (
        <span className="currency-value fw-semibold">
          {currencyFormat(original.total_price)}
        </span>
      ),
      meta: {
        headerProps: { style: { width: '8%' }, className: 'ps-4' },
        cellProps: { className: 'ps-4 fw-semibold text-body-highlight' }
      }
    },
    {
      id: 'commission',
      accessorKey: 'commission',
      header: t('transactions.detail.productColumns.commission', 'Commission'),
      visible: true,
      cell: ({ row: { original } }) => (
        <span className="currency-value text-danger">
          {currencyFormat(original.commission)}
        </span>
      ),
      meta: {
        headerProps: { style: { width: '8%' }, className: 'ps-4' },
        cellProps: { className: 'ps-4 text-danger' }
      }
    },
    {
      id: 'subtotal',
      accessorKey: 'subtotal',
      header: t('transactions.detail.productColumns.subtotal', 'Subtotal'),
      visible: true,
      cell: ({ row: { original } }) => (
        <span className="currency-value fw-bold">
          {currencyFormat(original.subtotal)}
        </span>
      ),
      meta: {
        headerProps: { style: { width: '8%' }, className: 'ps-4' },
        cellProps: { className: 'ps-4 fw-bold text-body-highlight' }
      }
    }
  ], []);

  return { orderColumns, productColumns };
}
